import type { LucideIcon } from "lucide-react";
import {
    Inbox,
    CalendarClock,
    CalendarCheck,
    Wrench,
    ClipboardCheck,
    CheckCircle2,
    Ban,
    AlertTriangle,
    DollarSign,
} from "lucide-react";
import { stageOfStatus, type LifecycleStageId } from "./vocabulary";

// ─── Lifecycle Stage Definitions ─────────────────────────────────────────
// The stage/status vocabulary itself (ids, status→stage mapping, groups)
// lives in ./vocabulary — this module adds the presentation layer (icons,
// colors, routes). Type + groups re-exported so existing importers keep
// working.

export type { LifecycleStageId } from "./vocabulary";
export { STAGE_STATUS_GROUPS } from "./vocabulary";

export interface LifecycleStage {
    id: LifecycleStageId;
    label: string;
    brandedLabel: string;
    icon: LucideIcon;
    badgeClass: string;
    hexColor: string;
    /**
     * Mode-adjusted text colors for the Work Pulse pills. The pill background
     * is hexColor at 10% opacity over the header strip, and several raw
     * hexColors fail WCAG AA (4.5:1) as text on that tint — too light in
     * light mode, too dark in dark mode. Omitted = hexColor passes as-is.
     */
    pillTextLight?: string;
    pillTextDark?: string;
    /** Primary action text shown on cards/buttons for this stage */
    actionLabel: string;
    /** Route the primary action navigates to */
    actionRoute: string;
}

export const LIFECYCLE_STAGES: LifecycleStage[] = [
    {
        id: "intake",
        label: "Intake",
        brandedLabel: "New Request",
        icon: Inbox,
        badgeClass:
            "bg-slate-50 text-slate-600 border border-slate-200 dark:bg-slate-800/50 dark:text-slate-300 dark:border-slate-700",
        hexColor: "#64748b",
        actionLabel: "Process Intake",
        actionRoute: "/jobs?view=lifecycle&stage=intake",
    },
    {
        id: "ready_to_schedule",
        label: "Ready to Schedule",
        brandedLabel: "Ready for Dispatch",
        icon: CalendarClock,
        badgeClass:
            "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800",
        hexColor: "#d97706",
        // Raw amber is 2.5:1 on the light header tint; dark tint passes as-is.
        pillTextLight: "#925800",
        actionLabel: "Open The Grid",
        actionRoute: "/dispatch",
    },
    {
        id: "scheduled",
        label: "Scheduled",
        brandedLabel: "Scheduled",
        icon: CalendarCheck,
        badgeClass:
            "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-800",
        hexColor: "#0284c7",
        pillTextLight: "#005682",
        pillTextDark: "#64bef0",
        actionLabel: "View Route",
        actionRoute: "/dispatch",
    },
    {
        id: "in_progress",
        label: "In Progress",
        brandedLabel: "In the Field",
        icon: Wrench,
        badgeClass:
            "bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800",
        hexColor: "#2563eb",
        pillTextLight: "#174099",
        pillTextDark: "#78a0ff",
        actionLabel: "Track Field Work",
        actionRoute: "/jobs?view=lifecycle&stage=in_progress",
    },
    {
        id: "needs_review",
        label: "Needs Review",
        brandedLabel: "Close Out",
        icon: ClipboardCheck,
        badgeClass:
            "bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800",
        hexColor: "#7c3aed",
        pillTextLight: "#5224a0",
        pillTextDark: "#b48cff",
        actionLabel: "Review Now",
        actionRoute: "/office-review",
    },
    {
        id: "completed",
        label: "Completed",
        brandedLabel: "Complete",
        icon: CheckCircle2,
        badgeClass:
            "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800",
        hexColor: "#059669",
        actionLabel: "View Record",
        actionRoute: "/jobs?view=lifecycle&stage=completed",
    },
    {
        id: "cancelled",
        label: "Cancelled",
        brandedLabel: "Cancelled",
        icon: Ban,
        badgeClass: "bg-muted text-foreground/80 border border-border",
        hexColor: "#94a3b8",
        actionLabel: "View Record",
        actionRoute: "/jobs?status=cancelled",
    },
    {
        id: "at_risk",
        label: "At Risk",
        brandedLabel: "At Risk",
        icon: AlertTriangle,
        badgeClass:
            "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800",
        hexColor: "#dc2626",
        actionLabel: "View Risks",
        actionRoute: "/jobs?view=lifecycle&stage=at_risk",
    },
    {
        id: "outstanding",
        label: "Outstanding",
        brandedLabel: "Outstanding Payments",
        icon: DollarSign,
        badgeClass:
            "bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800",
        hexColor: "#ea580c",
        actionLabel: "View Outstanding",
        actionRoute: "/commercial?tab=outstanding",
    },
];

export const STAGE_MAP = new Map(LIFECYCLE_STAGES.map((s) => [s.id, s]));

export function getStage(id: LifecycleStageId): LifecycleStage {
    return STAGE_MAP.get(id) ?? LIFECYCLE_STAGES[0];
}

// ─── Status → Lifecycle Stage Mapping ────────────────────────────────────
// Delegates to the shared vocabulary (single source, parity-tested against
// the server's copy in jobStatusMachine.ts).

export function mapStatusToStage(status: string | null | undefined): LifecycleStageId {
    return stageOfStatus(status);
}

export function mapStatusToStageObj(status: string | null | undefined): LifecycleStage {
    return getStage(mapStatusToStage(status));
}

// ─── Badge Class (drop-in replacement for old statusColor) ───────────────

export function lifecycleStatusColor(status: string | null | undefined): string {
    const s = (status ?? "").toLowerCase().trim();

    // Non-job statuses (invoice, estimate, etc.) keep their own semantics
    if (["paid", "active", "approved"].includes(s))
        return "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800";
    if (["rejected", "overdue", "failed", "void"].includes(s))
        return "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800";
    if (["sent", "open"].includes(s))
        return "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-800";
    if (s === "pending")
        return "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800";

    // Job lifecycle statuses
    const stage = mapStatusToStageObj(s);
    return stage.badgeClass;
}

// ─── Branded Label ───────────────────────────────────────────────────────

export function brandedLabel(status: string | null | undefined): string {
    const stage = mapStatusToStageObj(status);
    return stage.brandedLabel;
}

/** The ordered stages that represent the main workflow (excluding cancelled and at_risk) */
export const WORKFLOW_STAGES: LifecycleStageId[] = [
    "intake",
    "ready_to_schedule",
    "scheduled",
    "in_progress",
    "needs_review",
    "completed",
];
